import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  useDisclosure,
  Button,
  IconButton,
  ButtonGroup,
  Select,
  HStack,
  VStack,
  Circle,
  Text,
  Input,
  Spinner,
} from "@chakra-ui/react";
import LoadingTable from "../../components/LoadingTable";
import TableNoWithPagination from "../../components/TableNoWithPagination";
import {
  downloadFile,
  getProcessById,
  getProcesses,
  saveProcess,
} from "../../services/processService";
import { Tag } from "../../models/Filter";
import Filter from "../../components/Filter";
import { useMediaQuery } from "react-responsive";
import ProcessCard from "./components/ProcessCard";
import FilterModal from "../../components/FilterModal";
import { useNavigate } from "react-router-dom";
import {
  FaColumns,
  FaExpand,
  FaEye,
  FaFileExcel,
  FaHistory,
  FaPencilAlt,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import ColumnsModal from "./components/columnsModal";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { columns } from "./components/columns";
import { DateTime } from "luxon";
import { useCompany } from "../../contexts/CompanyContext";
import Layout from "../../components/Layout";
import { ProcessViewModel } from "../../models/Process";
import { processStatus } from "../Configurations/tabs/types";
import TagFilter from "../../components/TagFilter";
import ApproveModal from "./components/approveModal";
import AlterationStats from "./components/alterationStats";
import { getStatistics } from "../../services/historyService";
import UserAlterationsModal from "./components/UserAlterationsModal";
import MultipleSelect from "../../components/MultipleSelect";
import FullscreenTableModal from "./components/FullscreenTableModal";
import { crm } from "../../theme";
import DeleteProcessModal from "./components/deleteProcessModal";
import DeletionsModal from "./components/deletionsModal";
import {
  getUserPreferences,
  saveUserPreference,
  PREF_PROCESS_COLUMNS,
  PREF_PROCESS_FILTERS,
} from "../../services/userPreferenceService";
import { getTeams } from "../../services/teamService";
import { TeamViewModel } from "../../models/Team";

const Processes = () => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    columns.map((c) => c.label),
  );
  const [alterationStats, setAlterationStats] = useState({
    excelPercentage: 0,
    systemPercentage: 0,
    excelCount: 0,
    systemCount: 0,
  });
  const [clientView, setClientView] = useState(1);
  const [selectedProcess, setSelectedProcess] = useState<ProcessViewModel>();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState<Tag[]>([]);
  const [page, setPage] = useState<any[]>([]);
  const [hasMoreCards, setHasMoreCards] = useState(true);
  const [loadingMoreCards, setLoadingMoreCards] = useState(false);
  const cardPageIndexRef = useRef(1);
  const isFetchingCardsRef = useRef(false);
  const cardSentinelRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<boolean>();
  const [status, setStatus] = useState<string[]>([]);
  const [dateType, setDateType] = useState("openingDate");
  const [initialDate, setInitialDate] = useState<string>();
  const [finalDate, setFinalDate] = useState<string>();
  const [isUrgent, setIsUrgent] = useState<boolean>();

  const [importMode, setImportMode] = useState<string>();
  const [teamId, setTeamId] = useState<string>();
  const [teams, setTeams] = useState<TeamViewModel[]>([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<string>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenUsers,
    onOpen: onOpenUsers,
    onClose: onCloseUsers,
  } = useDisclosure();
  const {
    isOpen: isOpenColumns,
    onOpen: onOpenColumns,
    onClose: onCloseColumns,
  } = useDisclosure();
  const {
    isOpen: isOpenApprove,
    onOpen: onOpenApprove,
    onClose: onCloseApprove,
  } = useDisclosure();
  const {
    isOpen: isOpenFullscreen,
    onOpen: onOpenFullscreen,
    onClose: onCloseFullscreen,
  } = useDisclosure();
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenDeletions,
    onOpen: onOpenDeletions,
    onClose: onCloseDeletions,
  } = useDisclosure();

  const navigate = useNavigate();
  const auth = useAuthUser<any>();
  useEffect(() => {
    if (auth?.position > 1) {
      setClientView(2);
    }
  }, [auth]);

  const { currentCompany, currentEmulation } = useCompany();
  // Equipe é conceito da Distribuidora, mas usuário de empresa "Geral" enxerga tudo.
  // Clientes (position 0/1) não têm acesso à API de equipes.
  const canFilterByTeam = auth?.position > 1 && (currentCompany === 2 || auth?.company === "Geral");

  useEffect(() => {
    if (!canFilterByTeam) {
      setTeamId(undefined);
      return;
    }
    const loadTeams = async () => {
      const response = await getTeams();
      setTeams(response ?? []);
    };
    loadTeams();
  }, [canFilterByTeam]);

  // Load saved column/filter prefs once, then unblock the fetch effects below.
  useEffect(() => {
    const load = async () => {
      try {
        const prefs = await getUserPreferences();
        const columnsPref = prefs?.find((p) => p.key === PREF_PROCESS_COLUMNS);
        const filtersPref = prefs?.find((p) => p.key === PREF_PROCESS_FILTERS);
        const fixedLabel = columns.find((c) => c.fixed)?.label;

        if (columnsPref?.value) {
          const savedLabels: string[] = JSON.parse(columnsPref.value);
          const validLabels = savedLabels.filter((l) =>
            columns.some((c) => c.label === l),
          );
          if (validLabels.length > 0) {
            setSelectedColumns(
              fixedLabel && !validLabels.includes(fixedLabel)
                ? [fixedLabel, ...validLabels]
                : validLabels,
            );
          }
        } else {
          const legacy = localStorage.getItem("excel_columns");
          if (legacy) {
            try {
              setSelectedColumns(JSON.parse(legacy));
            } catch {
              // ignore malformed legacy value
            }
          }
        }

        if (filtersPref?.value) {
          const savedFilters = JSON.parse(filtersPref.value);
          if (savedFilters.tags) setTags(savedFilters.tags);
          if (savedFilters.status) setStatus(savedFilters.status);
          if (savedFilters.importMode !== undefined)
            setImportMode(savedFilters.importMode);
          if (savedFilters.active !== undefined)
            setActive(savedFilters.active);
          if (savedFilters.dateType) setDateType(savedFilters.dateType);
          if (savedFilters.teamId) setTeamId(savedFilters.teamId);
          if (savedFilters.isUrgent !== undefined)
            setIsUrgent(savedFilters.isUrgent);
        }
      } catch (error) {
        console.log("error loading user preferences", error);
      } finally {
        setPrefsLoaded(true);
      }
    };
    load();
  }, []);

  const visibleColumns = useMemo(() => {
    const fixedCol = columns.find((c) => c.fixed);
    const ordered = selectedColumns
      .filter((l) => l !== fixedCol?.label)
      .map((l) => columns.find((c) => c.label === l))
      .filter((c): c is (typeof columns)[number] => Boolean(c));
    return fixedCol ? [fixedCol, ...ordered] : ordered;
  }, [selectedColumns]);

  const handleColumnReorder = (fromLabel: string, toLabel: string) => {
    setSelectedColumns((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(fromLabel);
      const toIdx = next.indexOf(toLabel);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromLabel);
      saveUserPreference(PREF_PROCESS_COLUMNS, next);
      return next;
    });
  };

  const onCellEdit = async (rowId: any, index: string, newValue: any) => {
    try {
      // Get full process data
      const process = await getProcessById(rowId);

      // Update the specific item
      const updatedItems = process.items.map((item: any) =>
        item.id === rowId ? { ...item, [index]: newValue } : item,
      );

      // Save to backend with updated items
      await saveProcess({
        ...process,
        [index]: newValue,
        refId: (process.refId?.length ?? 0) === 0 ? undefined : process.refId,
        items: updatedItems,
      });
    } catch (error) {
      console.error("Error updating item:", error);
      throw error;
    }
  };

  const get = useCallback(async (
    localPageSize?: number,
    localPageNumber?: number,
    sortField?: string,
    sortDirection?: string,
    append?: boolean,
  ) => {
    if (!append) {
      setLoading(true);
      if (auth?.position === 2) {
        const responseStats = await getStatistics();
        setAlterationStats(responseStats);
      }
    }

    const localPs = localPageSize ?? 20;
    const localPn = localPageNumber ?? 0;

    const response = await getProcesses({
      pageSize: localPs,
      dateType,
      active,
      pageNumber: localPn,
      getAll: clientView > 1,
      getStatus: clientView === 3,
      multiStatus: status.join("|"),
      importMode,
      sortField,
      sortDirection,
      type: currentCompany,
      teamId,
      isUrgent,
      initialDate: initialDate,
      finalDate: finalDate,
      clientRef: tags
        .filter((t) => t.type === "clientRef")
        ?.map((t) => t.value)
        .join("|"),
      ncm: tags
        .filter((t) => t.type === "ncm")
        ?.map((t) => t.value)
        .join("|"),
      departurePort: tags
        .filter((t) => t.type === "departurePort")
        ?.map((t) => t.value)
        .join("|"),
      cvfRef: tags
        .filter((t) => t.type === "cvfRef")
        ?.map((t) => t.value)
        .join("|"),
      dischargePort: tags
        .filter((t) => t.type === "dischargePort")
        ?.map((t) => t.value)
        .join("|"),
      companyId: currentEmulation,
      countryOfOrigin: tags
        .filter((t) => t.type === "origin")
        ?.map((t) => t.value)
        .join("|"),
      exporter: tags
        .filter((t) => t.type === "supplier")
        ?.map((t) => t.value)
        .join("|"),
      responsible: tags
        .filter((t) => t.type === "responsible")
        ?.map((t) => t.value)
        .join("|"),
      insurance: tags
        .filter((t) => t.type === "insurance")
        ?.map((t) => t.value)
        .join("|"),
    });
    if (append) {
      setPage((prev) => [...prev, ...response?.data]);
    } else {
      setPage(response?.data);
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
    return response;
  }, [
    dateType,
    active,
    clientView,
    status,
    importMode,
    currentCompany,
    currentEmulation,
    initialDate,
    finalDate,
    tags,
    teamId,
    isUrgent,
    auth?.position,
  ]);
  useEffect(() => {
    if (!prefsLoaded) return;
    if (!(auth?.position === 0 || clientView === 1)) return;
    get().then((resp) => {
      if (resp) {
        setHasMoreCards(20 < resp.totalCount);
        cardPageIndexRef.current = 1;
        isFetchingCardsRef.current = false;
      }
    });
  }, [
    prefsLoaded,
    tags,
    clientView,
    currentEmulation,
    currentCompany,
    status,
    active,
    importMode,
    dateType,
    teamId,
    isUrgent,
  ]);

  useEffect(() => {
    if (!prefsLoaded) return;
    if (initialDate && finalDate) {
      get().then((resp) => {
        if (resp) {
          setHasMoreCards(20 < resp.totalCount);
          cardPageIndexRef.current = 1;
          isFetchingCardsRef.current = false;
        }
      });
    }
  }, [prefsLoaded, initialDate, finalDate]);

  useEffect(() => {
    if (!(auth?.position === 0 || clientView === 1)) return;
    const sentinel = cardSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreCards &&
          !loadingMoreCards &&
          !loading &&
          !isFetchingCardsRef.current
        ) {
          const index = cardPageIndexRef.current;
          isFetchingCardsRef.current = true;
          setLoadingMoreCards(true);
          get(20, index, undefined, undefined, true).then((resp) => {
            if (resp) {
              cardPageIndexRef.current = index + 1;
              setHasMoreCards((index + 1) * 20 < resp.totalCount);
            }
            isFetchingCardsRef.current = false;
            setLoadingMoreCards(false);
          });
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreCards, loadingMoreCards, loading, clientView, auth?.position]);

  // Debounced save — filters only, no dates (see plan: stale dates hiding data silently is worse than redigitating).
  useEffect(() => {
    if (!prefsLoaded) return;
    const timer = setTimeout(() => {
      saveUserPreference(PREF_PROCESS_FILTERS, {
        tags,
        status,
        importMode,
        active,
        dateType,
        teamId,
        isUrgent,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [prefsLoaded, tags, status, importMode, active, dateType, teamId, isUrgent]);

  const download = async () => {
    setLoadingFile(true);
    var file = await downloadFile({
      getAll: clientView > 1,
      columns: selectedColumns,
      sortField,
      sortDirection,
      type: currentCompany,
      active,
      initialDate: initialDate,
      finalDate: finalDate,
      clientRef: tags
        .filter((t) => t.type === "clientRef")
        ?.map((t) => t.value)
        .join("|"),
      ncm: tags
        .filter((t) => t.type === "ncm")
        ?.map((t) => t.value)
        .join("|"),
      departurePort: tags
        .filter((t) => t.type === "departurePort")
        ?.map((t) => t.value)
        .join("|"),
      cvfRef: tags
        .filter((t) => t.type === "cvfRef")
        ?.map((t) => t.value)
        .join("|"),
      dischargePort: tags
        .filter((t) => t.type === "dischargePort")
        ?.map((t) => t.value)
        .join("|"),
      companyId: currentEmulation,
      countryOfOrigin: tags
        .filter((t) => t.type === "origin")
        ?.map((t) => t.value)
        .join("|"),
      exporter: tags
        .filter((t) => t.type === "supplier")
        ?.map((t) => t.value)
        .join("|"),
      insurance: tags
        .filter((t) => t.type === "insurance")
        ?.map((t) => t.value)
        .join("|"),
    });
    const linkSource =`data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${file}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `Follow-Up ${DateTime.now().toFormat(
      "dd-LL-yyyy",
    )}`;
    downloadLink.click();
    setLoadingFile(false);
  };
  const visibleExtraFilters = [
    <Box>
      <Heading size="sm" mb="8px">Urgência</Heading>
      <Select
        value={isUrgent === undefined ? "all" : isUrgent ? "urgent" : "notUrgent"}
        onChange={(event) =>
          setIsUrgent(
            event.target.value === "all"
              ? undefined
              : event.target.value === "urgent",
          )
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value="all">Todos</option>
        <option value="urgent">Urgentes</option>
        <option value="notUrgent">Não urgentes</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Status
      </Heading>
      <MultipleSelect
        selectedValues={status}
        onChange={(e) => setStatus(e)}
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
        options={processStatus.map((p) => p.value)}
      />
    </Box>,
    <TagFilter
      {...{ tags, setTags }}
      type="clientRef"
      label="Ref. do cliente: "
    />,
  ];

  const extraFilters = [
    ...(canFilterByTeam
      ? [
        <Box>
          <Heading size="sm" mb="8px">
            Equipe
          </Heading>
          <Select
            value={teamId ?? ""}
            onChange={(e) =>
              !e.target.value
                ? setTeamId(undefined)
                : setTeamId(e.target.value)
            }
            backgroundColor="white"
            width={isMobile ? "80vw" : "16vw"}
          >
            <option value=""></option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Box>,
      ]
      : []),
    <TagFilter {...{ tags, setTags }} type="insurance" label="Seguro: " />,
    <Box>
      <Heading size="sm" mb="8px">
        Modo de importação
      </Heading>
      <Select
        value={importMode ?? ""}
        onChange={(e) =>
          !e.target.value
            ? setImportMode(undefined)
            : setImportMode(e.target.value)
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value=""></option>
        <option>Encomenda</option>
        <option>Distribuição</option>
        <option>Conta e Ordem</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Ativo
      </Heading>
      <Select
        value={active === undefined ? "" : String(active)}
        onChange={(e) =>
          !e.target.value
            ? setActive(undefined)
            : setActive(e.target.value === "true")
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value=""></option>
        <option value={"true"}>Ativo</option>
        <option value={"false"}>Inativo</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Tipo do período
      </Heading>
      <Select
        backgroundColor={"white"}
        width={isMobile ? "80vw" : "16vw"}
        value={dateType}
        onChange={(e) => setDateType(e.target.value)}
      >
        <option value="openingDate">Data de abertura</option>
        <option value="arrivalDate">Data de chegada</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Início do período
      </Heading>
      <Input
        backgroundColor={"white"}
        type="date"
        width={isMobile ? "80vw" : "16vw"}
        value={initialDate}
        onChange={(e) => setInitialDate(e.target.value)}
      />
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Fim do período
      </Heading>
      <Input
        backgroundColor={"white"}
        type="date"
        width={isMobile ? "80vw" : "16vw"}
        value={finalDate}
        onChange={(e) => setFinalDate(e.target.value)}
      />
    </Box>,
  ];
  return (
    <Box height="100%">
      <Layout>
        <Flex
          minHeight="100%"
          flexDirection="column"
          alignItems="center"
          backgroundColor="var(--main-bg-color)"
        >
          <Flex width="95%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box>
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                Processos
              </Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">
                Acompanhe e gerencie os processos de importação.
              </Text>
            </Box>
          </Flex>
          <FilterModal
            {...{
              extraFilters,
              isOpen,
              onClose,
              tags,
              setTags,
            }}
          />
          <Box
            p="32px"
            w="95%"
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
          >
            {isMobile ? (
              <Flex w="100%" justifyContent="center">
                <Button
                  w="80%"
                  backgroundColor="var(--icon-color)"
                  color="white"
                  onClick={onOpen}
                >
                  Filtrar documentos
                </Button>
              </Flex>
            ) : (
              <Flex width="95%">
                <Filter
                  {...{
                    extraFilters,
                    visibleExtraFilters,
                    tags,
                    setTags,
                  }}
                />
              </Flex>
            )}
          </Box>
          {auth?.position > 1 && (
            <>
              <Flex
                flexDir={{ base: "column", md: "row" }}
                w="95%"
                mt="32px"
                justifyContent="flex-end"
                gap="16px"
              >
                <Button
                  variant="outline"
                  borderRadius="10px"
                  borderColor={crm.border}
                  color={crm.text}
                  _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                  onClick={onOpenFullscreen}
                >
                  <Flex justifyContent="center" alignItems="center" gap="8px">
                    <FaExpand /> Tela cheia
                  </Flex>
                </Button>
                <Select
                  w="20vh"
                  value={clientView}
                  onChange={(e) => setClientView(parseFloat(e.target.value))}
                >
                  <option value={1}>Visão do cliente</option>
                  <option value={2}>Visão de tabela</option>
                  <option value={3}>Visão de status</option>
                </Select>
                <Button
                  color="white"
                  borderRadius="10px"
                  backgroundColor={crm.success}
                  _hover={{ backgroundColor: "#15803d" }}
                  onClick={() => download()}
                  isLoading={loadingFile}
                >
                  <Flex justifyContent="center" alignItems="center" gap="8px">
                    <FaFileExcel color="white" /> Gerar Follow-up
                  </Flex>
                </Button>
                <Button
                  variant="outline"
                  borderRadius="10px"
                  borderColor={crm.border}
                  color={crm.text}
                  _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                  onClick={() => onOpenColumns()}
                  isLoading={loadingFile}
                >
                  <Flex justifyContent="center" alignItems="center" gap="8px">
                    <FaColumns /> Personalizar colunas
                  </Flex>
                </Button>
                <ColumnsModal
                  {...{ selectedColumns, setSelectedColumns }}
                  isOpen={isOpenColumns}
                  onClose={onCloseColumns}
                  tags={tags}
                />
                <Button
                  color="white"
                  borderRadius="10px"
                  backgroundColor={crm.brand}
                  _hover={{ backgroundColor: crm.brandDark }}
                  onClick={() => navigate("/saveProcess")}
                >
                  <Flex justifyContent="center" alignItems="center" gap="8px">
                    <FaPlus color="white" /> Adicionar Processo
                  </Flex>
                </Button>
              </Flex>
              <FullscreenTableModal
                isOpen={isOpenFullscreen}
                onClose={onCloseFullscreen}
                visibleColumns={visibleColumns}
                tags={tags}
                isAdmin={auth.admin}
                onCellEdit={onCellEdit}
                onOpenApprove={onOpenApprove}
                setSelectedProcess={setSelectedProcess}
                onColumnReorder={handleColumnReorder}
                get={get}
              />
            </>
          )}

          {auth?.position === 0 || clientView === 1 ? (
            <>
              {loading ? (
                <Box width="95%" mt="64px">
                  <LoadingTable />
                </Box>
              ) : (
                <Flex width="95%" flexDir="column" mt="64px" gap="24px">
                  {page.map((p) => (
                    <ProcessCard {...{ p }} />
                  ))}
                </Flex>
              )}
              <div ref={cardSentinelRef} style={{ height: 1 }} />
              {loadingMoreCards && (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  py="24px"
                  gap="8px"
                >
                  <Spinner size="sm" color="var(--icon-color)" />
                  <Text fontSize="sm" color="gray.500">
                    Carregando mais...
                  </Text>
                </Flex>
              )}
              {!hasMoreCards && page.length > 0 && (
                <Flex justifyContent="center" py="16px" width="95%">
                  <Text fontSize="sm" color="gray.400">
                    Todos os registros foram carregados ({page.length} no total)
                  </Text>
                </Flex>
              )}
            </>
          ) : clientView === 2 ? (
            <Flex
              flexDir="column"
              width="95%"
              my="32px"
              p="32px"
              backgroundColor="white"
              border={`1px solid ${crm.border}`}
              borderRadius="12px"
              boxShadow={crm.shadowSm}
              overflow="hidden"
            >
              {auth?.position === 2 && (
                <Flex justifyContent="space-between" alignItems="center" mb="16px">
                  <AlterationStats
                    onOpenAlterations={onOpenUsers}
                    onOpenDeletions={onOpenDeletions}
                    excelPercentage={alterationStats.excelPercentage}
                    systemPercentage={alterationStats.systemPercentage}
                    excelCount={alterationStats.excelCount}
                    systemCount={alterationStats.systemCount}
                  />
                </Flex>
              )}
              <TableNoWithPagination
                onCellEdit={onCellEdit}
                isProcess
                get={get}
                tags={tags}
                sortField={sortField}
                setSortField={setSortField}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                reorderable
                onColumnReorder={handleColumnReorder}
                columns={[
                  ...visibleColumns,
                  {
                    label: "",
                    data: (p: any) => (
                      <ButtonGroup>
                        <IconButton
                          variant="ghost"
                          color={crm.brand}
                          _hover={{ bg: crm.brandSoft }}
                          aria-label="Editar"
                          icon={<FaPencilAlt />}
                          onClick={() => {
                            let blocked =
                              !auth.admin && p.status === "ENCERRADO";
                            if (!blocked)
                              navigate(`/saveProcess?id=${p?.id}`);
                          }}
                          opacity={
                            !auth.admin && p.status === "ENCERRADO" ? 0.4 : 1
                          }
                          cursor={
                            !auth.admin && p.status === "ENCERRADO"
                              ? "not-allowed"
                              : "pointer"
                          }
                        />
                        {auth.admin && (
                          <IconButton
                            variant="ghost"
                            color={crm.brand}
                            _hover={{ bg: crm.brandSoft }}
                            onClick={() => {
                              setSelectedProcess(p);
                              onOpenApprove();
                            }}
                            aria-label="Histórico"
                            icon={<FaHistory />}
                          />
                        )}
                        <a href={`/process?id=${p.id}`}>
                          <IconButton
                            variant="ghost"
                            color={crm.brand}
                            _hover={{ bg: crm.brandSoft }}
                            aria-label="Ver detalhes"
                            icon={<FaEye />}
                          />
                        </a>
                        {auth.admin && (
                          <IconButton
                            variant="ghost"
                            color="red"
                            onClick={() => {
                              setSelectedProcess(p);
                              onOpenDelete();
                            }}
                            aria-label="Excluir"
                            icon={<FaTrash />}
                          />
                        )}
                      </ButtonGroup>
                    ),
                  },
                ]}
                emptyMessage="Não há processos ainda"
              />
            </Flex>
          ) : (
            <Flex
              flexDir="column"
              width="95%"
              my="32px"
              p="32px"
              backgroundColor="white"
              border={`1px solid ${crm.border}`}
              borderRadius="12px"
              boxShadow={crm.shadowSm}
              overflow="hidden"
            >
              <TableNoWithPagination
                isProcess
                get={get}
                tags={tags}
                columns={[
                  {
                    label: "CVF Ref",
                    sortable: true,
                    data: (p: ProcessViewModel) => p.cvfRef,
                    index: "cvfRef",
                  },
                  {
                    label: "Cliente",
                    sortable: true,
                    data: (p: ProcessViewModel) => p.refName ?? p.clientName,
                    index: "clientName",
                  },
                  {
                    label: "Status",
                    sortable: false,
                    data: (p: ProcessViewModel) => {
                      let statusList = [...(p.statusList ?? [])];
                      if (p.status === "ENCERRADO") {
                        statusList.splice(statusList.length - 1, 0, {
                          status: "DATA DE CHEGADA",
                          dateTime: p.arrivalDate ?? "",
                        });
                      }

                      statusList = statusList.sort(
                        (a, b) =>
                          DateTime.fromISO(a.dateTime).toMillis() -
                          DateTime.fromISO(b.dateTime).toMillis(),
                      );

                      return (
                        <Flex>
                          {statusList.map((s) => (
                            <Box>
                              <Text
                                mb="2px"
                                ml="16px"
                                mr="24px"
                                color={s.status ? "black" : "white"}
                              >
                                {(s.status?.length ?? 0) === 0
                                  ? p.status
                                  : s.status}
                              </Text>
                              <HStack spacing={0}>
                                <Circle size="12px" bg="var(--icon-color)" />
                                <Box
                                  w="100%"
                                  bg="var(--icon-color)"
                                  h="2px"
                                ></Box>
                              </HStack>
                              <Text mt="2px" ml="16px" mr="24px">
                                {DateTime.fromISO(s.dateTime).toFormat(
                                  "dd/LL/yyyy",
                                )}
                              </Text>
                            </Box>
                          ))}
                          {p.status === "ENCERRADO" ? (
                            <HStack spacing={0}>
                              <VStack spacing={0}>
                                <Circle size="12px" bg="var(--icon-color)" />
                              </VStack>
                            </HStack>
                          ) : (
                            <Box>
                              <Text
                                mb="2px"
                                ml="16px"
                                mr="24px"
                                color={p.arrivalDate ? "black" : "white"}
                              >
                                DATA DE CHEGADA
                              </Text>
                              <HStack spacing={0}>
                                <VStack spacing={0}>
                                  {p.status === "ENTREGUE" ||
                                    p.status === "AGUARDANDO FECHAMENTO" ||
                                    p.status === "ENCERRADO" ? (
                                    <Circle
                                      size="12px"
                                      bg="var(--icon-color)"
                                    />
                                  ) : (
                                    <Circle
                                      size="12px"
                                      borderWidth={"2px"}
                                      borderColor="var(--icon-color)"
                                    />
                                  )}
                                </VStack>
                              </HStack>
                              <Text mt="2px" ml="16px" mr="24px">
                                {p.arrivalDate &&
                                  DateTime.fromISO(p.arrivalDate).toFormat(
                                    "dd/LL/yyyy",
                                  )}
                              </Text>
                            </Box>
                          )}
                        </Flex>
                      );
                    },
                    index: "clientName",
                  },
                ]}
                emptyMessage="Não há processos ainda"
              />
            </Flex>
          )}
        </Flex>
      </Layout>
      {auth?.position === 2 && (
        <UserAlterationsModal
          isOpen={isOpenUsers}
          onClose={onCloseUsers}
          companyId={currentEmulation}
          {...{
            initialDate,
            finalDate,
          }}
        />
      )}
      <ApproveModal
        isOpen={isOpenApprove}
        onClose={onCloseApprove}
        process={selectedProcess}
      />
      <DeleteProcessModal
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        process={selectedProcess}
        onDeleted={() => get()}
      />
      <DeletionsModal
        isOpen={isOpenDeletions}
        onClose={onCloseDeletions}
        onRestored={() => get()}
      />
    </Box>
  );
};

export default Processes;
